import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ChevronDown } from "lucide-react";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { label: "Velkommen", href: "/" },
    { label: "Kørekort", href: "/korekort" },
    { label: "Priser", href: "/priser" },
    {
      label: "Teori",
      href: "/teori",
      dropdown: [
        { label: "Teori & Prøvekrav", href: "/teori" },
        { label: "Køreteknisk anlæg", href: "/koreteknisk-anlaeg" },
      ]
    },
    { label: "Førstehjælp", href: "/forstehjaelp" },
    { label: "Bilen & kørelæreren", href: "/om-carina" },
    { label: "Holdstart & tilmelding", href: "/kontakt" },
  ];

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-stone-200 bg-white/95 backdrop-blur-md">
      {/* Top Bar with Contact Info */}
      <div className="bg-brand-dark text-stone-200 text-xs py-2 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Phone className="h-3.5 w-3.5 text-brand-secondary" />
              Tlf: <a href="tel:30517269" className="hover:text-white transition-colors">30 51 72 69</a>
            </span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Mail className="h-3.5 w-3.5 text-brand-secondary" />
              Email: <a href="mailto:carinaskoereskole@gmail.com" className="hover:text-white transition-colors">carinaskoereskole@gmail.com</a>
            </span>
          </div>
          <div className="text-stone-400 text-[11px] font-medium uppercase tracking-wider">
            Gugvej 186 D, kld. tv., 9210 Aalborg SØ
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img
              src="https://assets.auxodev.dk/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/logo3-carinas-k-c3-b8reskole-orange-outline-j.jpg"
              alt="Carinas Køreskole"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
            {menuItems.map((item) => {
              if (item.dropdown) {
                return (
                  <div key={item.href} className="relative group">
                    <button
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer ${
                        isActive(item.href)
                          ? "text-brand-primary"
                          : "text-stone-600 hover:text-brand-primary"
                      }`}
                    >
                      {item.label}
                      <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                    </button>
                    {/* Dropdown Menu */}
                    <div className="absolute left-0 mt-1 w-56 rounded-xl bg-white border border-stone-100 shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem.href}
                          to={subItem.href}
                          className={`block px-4 py-2 text-sm font-medium transition-all duration-200 ${
                            isActive(subItem.href)
                              ? "bg-brand-primary/10 text-brand-primary"
                              : "text-stone-600 hover:text-brand-primary hover:bg-stone-50"
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    isActive(item.href)
                      ? "text-brand-primary"
                      : "text-stone-600 hover:text-brand-primary"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop Menu & Mobile Menu Button Wrapper */}
          <div className="flex items-center gap-4">
            <Link
              to="/kontakt#tilmelding-form"
              className="hidden sm:inline-block px-5 py-2.5 bg-brand-primary text-white text-sm font-bold rounded-xl shadow-sm hover:bg-brand-primary-hover hover:shadow transition-all duration-200"
            >
              Tilmeld dig nu
            </Link>

            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="lg:hidden inline-flex items-center justify-center p-2.5 rounded-lg text-stone-500 hover:text-brand-primary hover:bg-stone-50 focus:outline-none transition-all duration-200 cursor-pointer"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Åbn menu</span>
              {isOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile "Tilmeld dig nu" Button Row */}
      <div className="sm:hidden px-4 pb-4">
        <Link
          to="/kontakt#tilmelding-form"
          className="block w-full text-center py-3 bg-brand-primary text-white text-sm font-bold rounded-xl shadow-sm hover:bg-brand-primary-hover hover:shadow transition-all duration-200"
        >
          Tilmeld dig nu
        </Link>
      </div>

      {/* Menu Dropdown */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-y-auto ${
          isOpen
            ? "max-h-[calc(100vh-180px)] sm:max-h-[calc(100vh-120px)] border-t border-stone-100 opacity-100 visible"
            : "max-h-0 opacity-0 invisible overflow-hidden"
        }`}
        id="mobile-menu"
      >
        <div className="px-4 pt-3 pb-6 space-y-1.5 bg-white shadow-inner">
          {menuItems.map((item) => {
            if (item.dropdown) {
              return (
                <div key={item.href} className="space-y-1">
                  <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-stone-400 mt-2">
                    Mere info
                  </div>
                  {item.dropdown.map((subItem) => (
                    <Link
                      key={subItem.href}
                      to={subItem.href}
                      onClick={() => setIsOpen(false)}
                      className={`block px-6 py-2.5 rounded-lg text-base font-semibold transition-all duration-200 ${
                        isActive(subItem.href)
                          ? "bg-brand-primary/10 text-brand-primary"
                          : "text-stone-600 hover:text-brand-primary hover:bg-stone-50"
                      }`}
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              );
            }

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all duration-200 ${
                  isActive(item.href)
                    ? "bg-brand-primary/10 text-brand-primary"
                    : "text-stone-600 hover:text-brand-primary hover:bg-stone-50"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    </header>
  );
}
